import type { BrandConfig } from '@portals-platform/brand-kit';

/**
 * Brinks brand configuration for the Certificate Portal.
 *
 * All secrets (Auth0 credentials, API URL) are read from environment variables
 * which are injected at build time from Azure DevOps variable groups.
 *
 * Required env vars — see .env.example for the full list.
 */
export const brinksConfig: BrandConfig = {
  id: 'brinks-certificate-portal',
  name: 'Brinks',
  domain: import.meta.env.VITE_BRAND_DOMAIN ?? 'portal.brinks.com',

  // Leave Auth0 fields off when the env var is unset. `?? ''` fails
  // BrandConfigSchema's min(1) checks, and the validate job has no VITE_* group.
  auth: {
    ...(import.meta.env.VITE_AUTH0_DOMAIN ? { domain: import.meta.env.VITE_AUTH0_DOMAIN } : {}),
    ...(import.meta.env.VITE_AUTH0_CLIENT_ID
      ? { clientId: import.meta.env.VITE_AUTH0_CLIENT_ID }
      : {}),
    ...(import.meta.env.VITE_AUTH0_AUDIENCE
      ? { audience: import.meta.env.VITE_AUTH0_AUDIENCE }
      : {}),
    redirectUri: `${window.location.origin}/callback`,
    logoutReturnTo: window.location.origin,
  },

  api: {
    baseUrl: import.meta.env.VITE_API_BASE_URL ?? 'https://api.brinks.com',
    timeoutMs: 30_000,
  },

  assets: {
    logoUrl: '/brinks-logo-blue.png',
    logoInverseUrl: '/brinks-logo-blue.png',
    faviconUrl: '/favicon.ico',
    fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
  },

  features: {
    certificateDownload: true,
    bulkOperations: false,
    analyticsDashboard: false,
    userManagement: false,
  },

  // ThemeTokens metadata — actual values are in theme.css
  theme: {
    colorPrimary: 'var(--color-primary)',
    colorPrimaryForeground: 'var(--color-primary-foreground)',
    colorSecondary: 'var(--color-secondary)',
    colorSecondaryForeground: 'var(--color-secondary-foreground)',
    colorBackground: 'var(--color-background)',
    colorForeground: 'var(--color-foreground)',
    colorSurface: 'var(--color-surface)',
    colorSurfaceForeground: 'var(--color-surface-foreground)',
    colorBorder: 'var(--color-border)',
    colorMuted: 'var(--color-muted)',
    colorMutedForeground: 'var(--color-muted-foreground)',
    colorDestructive: 'var(--color-destructive)',
    colorDestructiveForeground: 'var(--color-destructive-foreground)',
    radiusSm: 'var(--radius-sm)',
    radiusMd: 'var(--radius-md)',
    radiusLg: 'var(--radius-lg)',
  },

  i18n: {
    defaultLocale: 'en',
    supportedLocales: ['en'],
    localesPath: '/locales',
  },
};
